import { motion, useReducedMotion } from "framer-motion";
import { QRCodeSVG } from "qrcode.react";
import { Link } from "react-router-dom";
import { ArrowRight, Mic, Image as ImageIcon } from "lucide-react";
import { STORES } from "../content/shared";
import { useT } from "../i18n";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "./ui/accordion";

const SSR = typeof window === "undefined";
const ease = [0.22, 1, 0.36, 1];

// Scroll reveal: content is plain on the server; motion is only added on the client.
export const Reveal = ({ children, delay = 0, y = 24, className = "", as = "div" }) => {
  const reduce = useReducedMotion();
  if (SSR || reduce) {
    const Tag = as;
    return <Tag className={className}>{children}</Tag>;
  }
  const M = motion[as];
  return (
    <M className={className} initial={{ opacity: 0, y }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.12 }} transition={{ duration: 0.7, ease, delay }}>
      {children}
    </M>
  );
};

// Masked line-by-line headline reveal used on page heroes.
export const MaskHeadline = ({ lines, className = "", testid }) => {
  const reduce = useReducedMotion();
  const list = Array.isArray(lines) ? lines : [lines];
  return (
    <h1 className={className} data-testid={testid}>
      {list.map((l, i) => (
        <span key={i} className="block overflow-hidden pb-[0.08em] -mb-[0.08em]">
          {SSR || reduce ? (
            <span className="block">{l}</span>
          ) : (
            <motion.span className="block" initial={{ y: "105%" }} animate={{ y: 0 }} transition={{ duration: 0.9, ease, delay: 0.08 + i * 0.12 }}>
              {l}
            </motion.span>
          )}
        </span>
      ))}
    </h1>
  );
};

export const EuCircle = ({ className = "h-5 w-5" }) => (
  <svg viewBox="0 0 24 24" className={className} aria-hidden>
    <circle cx="12" cy="12" r="11" fill="#003399" stroke="#FFCC00" strokeWidth="1.5" />
    {Array.from({ length: 12 }).map((_, i) => {
      const a = (i * Math.PI) / 6;
      return <circle key={i} cx={12 + 6.2 * Math.sin(a)} cy={12 - 6.2 * Math.cos(a)} r="1.15" fill="#FFCC00" />;
    })}
  </svg>
);

export const InViewTitle = ({ lines, className = "", as = "h2" }) => {
  const reduce = useReducedMotion();
  const Tag = as;
  const list = Array.isArray(lines) ? lines : [lines];
  if (SSR || reduce) return <Tag className={className}>{list.join(" ")}</Tag>;
  return (
    <motion.div initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.15 }}>
      <Tag className={className}>
        {list.map((l, i) => (
          <span key={i} className="block overflow-hidden pb-[0.08em] -mb-[0.08em]">
            <motion.span className="block" variants={{ hidden: { y: "105%" }, show: { y: 0, transition: { duration: 0.8, ease, delay: 0.1 + i * 0.1 } } }}>{l}</motion.span>
          </span>
        ))}
      </Tag>
    </motion.div>
  );
};

export const StepNum = ({ n }) => {
  const reduce = useReducedMotion();
  const label = String(n).padStart(2, "0");
  if (SSR || reduce) return <p className="flex items-center gap-3 font-mono text-sm text-primary">{label}<span className="h-px w-16 bg-primary" /></p>;
  return (
    <motion.p className="flex items-center gap-3 font-mono text-sm text-primary" initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.5 }}>
      <motion.span variants={{ hidden: { opacity: 0, x: -12 }, show: { opacity: 1, x: 0, transition: { duration: 0.5, ease } } }}>{label}</motion.span>
      <motion.span className="h-px w-16 origin-left bg-primary" variants={{ hidden: { scaleX: 0 }, show: { scaleX: 1, transition: { duration: 0.8, ease, delay: 0.2 } } }} />
    </motion.p>
  );
};

export const Plate = ({ text, size = "md", className = "" }) => {
  const s = size === "lg" ? "h-14 text-2xl gap-3 px-3" : size === "sm" ? "h-8 text-sm gap-2 px-2" : "h-11 text-lg gap-2.5 px-2.5";
  const c = size === "lg" ? "h-8 w-8" : size === "sm" ? "h-5 w-5" : "h-6 w-6";
  return (
    <span className={`inline-flex items-center whitespace-nowrap bg-transparent ${s} ${className}`} aria-label={text}>
      <EuCircle className={`${c} shrink-0`} />
      <span className="pr-1 font-plate font-bold tracking-[0.08em]">{text}</span>
    </span>
  );
};

// Illustrative conversation. Always labelled so it is not mistaken for real data.
export const Chat = ({ messages = [], plate, className = "", testid }) => {
  const { t } = useT();
  return (
    <figure className={`rounded-2xl border border-border bg-surface p-4 shadow-[0_20px_50px_-30px_rgba(23,25,64,0.45)] ${className}`} data-testid={testid}>
      <div className="mb-3 flex items-center justify-between gap-3">
        {plate ? <Plate text={plate} size="sm" /> : <span />}
        <span className="rounded bg-signal/25 px-2 py-0.5 text-[11px] font-medium uppercase tracking-wider text-foreground">{t.common.illustration}</span>
      </div>
      <div className="space-y-2">
        {messages.map(([who, text, kind], i) => (
          <div key={i} className={`flex ${who === "me" ? "justify-end" : "justify-start"}`}>
            <p className={`max-w-[85%] rounded-2xl px-3.5 py-2 text-sm leading-snug ${who === "me" ? "rounded-br-sm bg-primary text-primary-foreground" : "rounded-bl-sm bg-surface-2 text-foreground"}`}>
              {kind === "voice" && <Mic className="mr-1.5 inline h-3.5 w-3.5" aria-hidden />}
              {kind === "photo" && <ImageIcon className="mr-1.5 inline h-3.5 w-3.5" aria-hidden />}
              {text}
            </p>
          </div>
        ))}
      </div>
      <figcaption className="sr-only">{t.common.illustration}</figcaption>
    </figure>
  );
};

// Real app screenshot in a quiet frame (screens already include the app UI).
export const Screen = ({ src, alt, className = "", eager = false }) => (
  <div className={`overflow-hidden rounded-[1.75rem] border border-[#2a2d66] bg-[#171940] p-1.5 shadow-[0_40px_80px_-40px_rgba(23,25,64,0.6)] ${className}`}>
    <img src={src} alt={alt} width="521" height="1024" loading={eager ? "eager" : "lazy"} decoding="async" className="block h-auto w-full rounded-[1.4rem]" />
  </div>
);

export const StoreBadges = ({ className = "", prefix = "store" }) => {
  const { t } = useT();
  return (
    <div className={`flex flex-wrap items-center gap-3 ${className}`}>
      <a href={STORES.ios} target="_blank" rel="noopener noreferrer" data-testid={`${prefix}-appstore-link`} className="rounded-lg transition-transform hover:-translate-y-0.5">
        <img src="/images/appstore-badge.png" alt={t.common.appStore} width="135" height="40" className="h-11 w-auto" />
      </a>
      <a href={STORES.android} target="_blank" rel="noopener noreferrer" data-testid={`${prefix}-googleplay-link`} className="rounded-lg transition-transform hover:-translate-y-0.5">
        <img src="/images/googleplay-badge.png" alt={t.common.googlePlay} width="180" height="53" className="h-11 w-auto" />
      </a>
    </div>
  );
};

export const QR = ({ url, label, testid }) => (
  <figure className="inline-flex flex-col items-center gap-2" data-testid={testid}>
    <div className="rounded-xl border border-border bg-white p-3">
      <QRCodeSVG value={url} size={132} fgColor="#171940" bgColor="#ffffff" level="M" title={label} />
    </div>
    <figcaption className="text-xs text-muted-foreground">{label}</figcaption>
  </figure>
);

export const SectionHead = ({ eyebrow, title, lead, className = "", align = "left" }) => (
  <Reveal className={`${align === "center" ? "mx-auto text-center" : ""} max-w-3xl ${className}`}>
    {eyebrow && <p className="eyebrow mb-4">{eyebrow}</p>}
    <h2>{title}</h2>
    {lead && <p className={`lead mt-5 ${align === "center" ? "mx-auto" : ""}`}>{lead}</p>}
  </Reveal>
);

export const ArrowLink = ({ to, children, testid, className = "" }) => (
  <Link to={to} data-testid={testid} className={`group inline-flex items-center gap-1.5 text-sm font-medium text-primary ${className}`}>
    {children}
    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" aria-hidden />
  </Link>
);

export const Marquee = ({ items = [] }) => (
  <div className="marquee overflow-hidden border-y border-border bg-surface py-5" aria-hidden>
    <div className="marquee-track flex w-max items-center gap-10 whitespace-nowrap">
      {[...items, ...items].map((it, i) => (
        <span key={i} className="flex items-center gap-10 font-display text-2xl text-foreground/80 md:text-3xl">
          {it}
          <span className="h-2 w-2 rounded-full bg-primary" />
        </span>
      ))}
    </div>
  </div>
);

export const FaqList = ({ items = [], prefix = "faq" }) => (
  <Accordion type="single" collapsible className="divide-y divide-border border-y border-border">
    {items.map(([q, a], i) => (
      <AccordionItem key={i} value={`${prefix}-${i}`} className="border-0">
        <AccordionTrigger data-testid={`${prefix}-q-${i}`} className="py-5 text-left font-display text-lg font-semibold hover:no-underline">
          {q}
        </AccordionTrigger>
        <AccordionContent forceMount className="pb-5 text-base leading-relaxed text-muted-foreground">
          <p className="max-w-3xl">{a}</p>
        </AccordionContent>
      </AccordionItem>
    ))}
  </Accordion>
);

export const PageHero = ({ eyebrow, lines, lead, children, aside, testid = "page-hero" }) => (
  <section className="container-x pb-12 pt-14 md:pb-20 md:pt-24" data-testid={testid}>
    <div className={`grid gap-12 ${aside ? "lg:grid-cols-[1fr_1.1fr] lg:items-end" : ""}`}>
      <div>
        {eyebrow && <p className="eyebrow mb-6">{eyebrow}</p>}
        <MaskHeadline lines={lines} className="max-w-4xl" testid={`${testid}-title`} />
        {lead && <Reveal delay={0.25}><p className="lead mt-6">{lead}</p></Reveal>}
        {children}
      </div>
      {aside}
    </div>
  </section>
);
